import {
  newKey,
  parseBuilderDocument,
  serializeBuilder,
  type BuilderCatalog,
  type ConditionNode,
  type GroupNode,
  type GroupOperator,
} from '../alert-rules/alert-condition'

export type ConsumableCondition = {
  threshold: ConditionNode
  filters: ConditionNode[]
  filterOperator: GroupOperator
}

export function defaultConsumableCondition(): ConsumableCondition {
  return {
    threshold: {
      kind: 'condition',
      key: newKey(),
      field: '',
      op: '',
      value: '',
    },
    filters: [],
    filterOperator: 'and',
  }
}

export function readConsumableCondition(
  catalog: BuilderCatalog,
  document: unknown,
): ConsumableCondition | null {
  const root = parseBuilderDocument(catalog, document)
  if (!root || root.op !== 'and') return null
  const [threshold, ...remaining] = root.children
  if (
    threshold?.kind !== 'condition' ||
    threshold.field !== 'supply.percentRemaining' ||
    threshold.op === 'in' ||
    typeof threshold.value !== 'string'
  )
    return null
  const filterGroup =
    remaining.length === 1 && remaining[0]?.kind === 'group'
      ? remaining[0]
      : null
  const filters = filterGroup ? filterGroup.children : remaining
  if (
    !filters.every(
      (node): node is ConditionNode =>
        node.kind === 'condition' &&
        node.field.startsWith('supply.') &&
        node.field !== 'supply.percentRemaining',
    )
  )
    return null
  return { threshold, filters, filterOperator: filterGroup?.op ?? 'and' }
}

export function consumableDocument(
  catalog: BuilderCatalog,
  value: ConsumableCondition,
) {
  const filterNodes =
    value.filters.length > 1
      ? [
          {
            kind: 'group',
            key: 'supply-filters',
            op: value.filterOperator,
            children: value.filters,
          } satisfies GroupNode,
        ]
      : value.filters
  const root: GroupNode = {
    kind: 'group',
    key: 'consumable-condition',
    op: 'and',
    children: [value.threshold, ...filterNodes],
  }
  return serializeBuilder(catalog, root)
}
